/**
 * Collapses concurrent calls that ask for the same thing.
 *
 * A data action's cache cannot do this: callers that arrive before the first call resolves all miss
 * a cache nothing has populated yet, so each one issues its own request. This keys on the work rather
 * than the result, so the second caller awaits the first call instead of starting another, and the
 * entry is released as soon as it settles so a later genuine refresh still runs.
 */
const inFlight = new Map<string, Promise<unknown>>();

export const dedupeInFlight = <T>(key: string, start: () => Promise<T>): Promise<T> => {
    const existing = inFlight.get(key) as Promise<T> | undefined;
    if (existing) {
        return existing;
    }

    const release = (): void => {
        inFlight.delete(key);
    };

    const started = start().then(
        result => {
            release();
            return result;
        },
        error => {
            release();
            throw error;
        }
    );

    inFlight.set(key, started);

    return started;
};
